import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Star, ShieldCheck, ArrowRight, SlidersHorizontal, X, Hospital as HospitalIcon, Navigation } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import { hospitalApi } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";
import { useLocationStore } from "@/store/locationStore";
import LocationPickerDialog from "@/components/LocationPickerDialog";
import SavePreferredButton from "@/components/SavePreferredButton";

const SERVICE_CHIPS = ["Dialysis", "MRI", "CT Scan", "X-Ray", "Ultrasound"];
const DISTANCES = [0, 2, 5, 10, 15, 25];
const AVATAR_COLORS = ["#2563EB", "#7C3AED", "#EC4899", "#16A34A", "#0EA5E9", "#F59E0B", "#EF4444"];
const BM_KEY = "trata_bookmarked_hospitals";

const initials = (n = "") => n.replace(/^(the|dr\.?)\s+/i, "").split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
const avatarColor = (id = "") => AVATAR_COLORS[Math.abs([...id].reduce((a, c) => a + c.charCodeAt(0), 0)) % AVATAR_COLORS.length];

export default function FindHospitals() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const coords = useLocationStore((s) => s.coords);
  const locCity = useLocationStore((s) => s.city);
  const locLabel = useLocationStore((s) => s.label);
  const detectLocation = useLocationStore((s) => s.detect);
  const locText = locLabel || locCity || "your area";

  const [loading, setLoading] = useState(true);
  const [hospitals, setHospitals] = useState([]);
  const [search, setSearch] = useState("");
  const [activeChip, setActiveChip] = useState(null);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [sortMode, setSortMode] = useState("nearest");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [distanceIdx, setDistanceIdx] = useState(0);
  const [bookmarks] = useState(() => {
    try { return JSON.parse(localStorage.getItem(BM_KEY) || "[]"); } catch { return []; }
  });

  const load = useCallback(async (extra = {}) => {
    setLoading(true);
    try {
      const params = {
        ...(coords?.latitude ? { lat: coords.latitude, lng: coords.longitude } : {}),
        ...(user?.phone ? { requester_phone: user.phone } : {}),
        ...extra,
      };
      const res = await hospitalApi.browse(params);
      setHospitals(res.data?.hospitals || []);
    } catch { setHospitals([]); } finally { setLoading(false); }
  }, [coords, user]);

  // Use the shared location store (same as Home / Book Lab Test); detect once if unset.
  useEffect(() => { if (!coords?.latitude) detectLocation(); }, [coords, detectLocation]);
  useEffect(() => { load(); }, [load]);

  const runSearch = (term) => load(term ? { service: term } : {});
  const onChip = (chip) => {
    if (activeChip === chip) { setActiveChip(null); runSearch(search || null); }
    else { setActiveChip(chip); setSearch(""); runSearch(chip); }
  };

  const maxKm = DISTANCES[distanceIdx];
  const display = [...hospitals]
    .filter((h) => (verifiedOnly ? h.is_sanjeevani_verified !== false : true))
    .filter((h) => (maxKm === 0 ? true : (h.distance_km ?? 9999) <= maxKm))
    .sort((a, b) => {
      const ab = bookmarks.includes(a.id) ? 0 : 1, bb = bookmarks.includes(b.id) ? 0 : 1;
      if (ab !== bb) return ab - bb;
      if (sortMode === "rating") return (b.rating || 0) - (a.rating || 0);
      if (sortMode === "nearest") return (a.distance_km ?? 9999) - (b.distance_km ?? 9999);
      return (a.name || "").localeCompare(b.name || "");
    });

  const Chip = ({ active, onClick, children, testid }) => (
    <button onClick={onClick} data-testid={testid}
      className={`whitespace-nowrap flex items-center gap-1.5 px-4 h-9 rounded-full text-sm font-medium border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-muted text-[#4b5563] border-transparent hover:bg-accent"}`}>
      {children}
    </button>
  );

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold">Find Hospitals</h1>
        <p className="text-sm text-muted-foreground mt-1">Verified hospitals & services near you</p>
      </div>

      {/* Location bar — same UX as Book a Lab Test */}
      <div className="flex items-center justify-between rounded-2xl border border-border bg-card px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <MapPin className="h-4 w-4 text-primary shrink-0" />
          <p className="text-sm min-w-0 truncate">
            <span className="text-muted-foreground">Showing hospitals for </span>
            <span className="font-semibold">{locText}</span>
          </p>
        </div>
        <button className="text-sm font-semibold text-primary flex items-center gap-1 shrink-0" onClick={() => setShowLocationPicker(true)} data-testid="change-location">
          <Navigation className="h-3.5 w-3.5" /> Change
        </button>
      </div>

      {/* Smart search */}
      <div className="flex items-center rounded-2xl bg-muted px-4 h-12 focus-within:ring-1 focus-within:ring-ring">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input data-testid="hospital-search" value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { setActiveChip(null); runSearch(search || null); } }}
          placeholder="e.g. Apollo, heart hospital, knee pain..."
          className="border-0 bg-transparent focus-visible:ring-0 shadow-none" />
        {search && <button onClick={() => { setSearch(""); runSearch(null); }} data-testid="clear-search"><X className="h-4 w-4 text-muted-foreground" /></button>}
      </div>

      {/* Service chips */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-0.5">
        {SERVICE_CHIPS.map((chip) => (
          <Chip key={chip} active={activeChip === chip} onClick={() => onChip(chip)} testid={`service-chip-${chip.toLowerCase().replace(/\s/g, "-")}`}>{chip}</Chip>
        ))}
      </div>

      {/* Sort / filter chips */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-0.5">
        <Chip active={sortMode === "nearest"} onClick={() => setSortMode((p) => (p === "nearest" ? null : "nearest"))} testid="sort-nearest"><MapPin className="h-4 w-4" /> Nearest</Chip>
        <Chip active={sortMode === "rating"} onClick={() => setSortMode((p) => (p === "rating" ? null : "rating"))} testid="sort-rating"><Star className="h-4 w-4" /> Top Rated</Chip>
        <Chip active={verifiedOnly} onClick={() => setVerifiedOnly((v) => !v)} testid="verified-filter"><ShieldCheck className="h-4 w-4" /> {verifiedOnly ? "Verified" : "Verified?"}</Chip>
        <Chip active={maxKm !== 0} onClick={() => setDistanceIdx((i) => (i + 1) % DISTANCES.length)} testid="distance-filter"><SlidersHorizontal className="h-4 w-4" /> {maxKm === 0 ? "Distance" : `Within ${maxKm} km`}</Chip>
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-40 rounded-3xl" />)}</div>
      ) : display.length === 0 ? (
        <Card className="rounded-3xl p-8 text-center border-dashed">
          <HospitalIcon className="h-10 w-10 text-muted-foreground mx-auto" />
          <p className="mt-3 font-medium">No hospitals found</p>
          <p className="text-sm text-muted-foreground mt-1">{activeChip ? `No hospitals offer ${activeChip} nearby.` : "Try a different search."}</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {display.map((h) => {
            const open = h.emergencyAvailable !== false && h.is_open !== false;
            return (
              <Card key={h.id} onClick={() => navigate(`/hospital/${h.id}`)} data-testid={`hospital-card-${h.id}`}
                className="relative rounded-3xl p-5 cursor-pointer hover:shadow-md transition-all">
                <div className="absolute top-4 right-4 z-10">
                  <SavePreferredButton userId={user?._id} provider={{ id: h.id, name: h.name, city: h.city, state: h.state, address: h.address, logoUrl: h.logoUrl }} category="hospital" variant="icon" />
                </div>
                <div className="flex items-start gap-4">
                  <div className="h-14 w-14 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: avatarColor(h.id) }}>
                    <span className="text-white font-semibold">{initials(h.name)}</span>
                  </div>
                  <div className="min-w-0 flex-1 pr-6">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold truncate">{h.name}</p>
                      {h.is_sanjeevani_verified !== false && (
                        <span className="flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ backgroundColor: "#CCFBF1", color: "#005C66" }}>
                          <ShieldCheck className="h-3 w-3" /> Verified by Trata
                        </span>
                      )}
                    </div>
                    {(h.address || h.city) && <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{[h.address, h.city].filter(Boolean).join(", ")}</p>}
                    <div className="flex items-center gap-3 mt-2 text-sm">
                      <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5" style={{ color: "#f59e0b" }} fill="#f59e0b" /> {Number(h.rating || 4.5).toFixed(1)}</span>
                      {typeof h.distance_km === "number" && <span className="flex items-center gap-1 text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {h.distance_km.toFixed(1)} km</span>}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: open ? "#DCFCE7" : "#FEE2E2", color: open ? "#059669" : "#dc2626" }}>
                        {open ? "🟢 Open Now" : "🔴 Closed"}
                      </span>
                      {h.services_count ? <span className="text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground">🔬 {h.services_count} Services</span> : null}
                      {h.specialists_count ? <span className="text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground">🩺 {h.specialists_count} Doctors</span> : null}
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-center gap-2 h-11 rounded-2xl bg-primary text-primary-foreground font-medium" data-testid={`primary-action-${h.id}`}>
                  View Hospital <ArrowRight className="h-4 w-4" />
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <LocationPickerDialog open={showLocationPicker} onOpenChange={setShowLocationPicker} />
    </div>
  );
}
